class AnnouncementCarousel extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.autoplayInterval = null;
    this.currentIndex = 0;
    this.isHovered = false;
    this.slides = null;
    this.autoplayDelay = null;
    this.loop = null;
    
    // Touch/swipe properties
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchEndX = null;
    this.touchEndY = null;
    this.minSwipeDistance = 50;

    // Bind methods to preserve 'this' context
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.scrollPrev = this.scrollPrev.bind(this);
    this.scrollNext = this.scrollNext.bind(this);
    this.togglePrevNextBtns = this.togglePrevNextBtns.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    // Clean up Embla instance
    if (this.emblaApi) {
      this.emblaApi.off('select', this.togglePrevNextBtns);
      this.emblaApi.off('init', this.togglePrevNextBtns);
      this.emblaApi.off('reInit', this.togglePrevNextBtns);
      this.emblaApi.destroy();
      this.emblaApi = null;
    }

    // Clean up fade interval
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }

    // Remove event listeners
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.handlePrevClick);
      this.prevBtn.removeEventListener('click', this.scrollPrev);
    }
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.handleNextClick);
      this.nextBtn.removeEventListener('click', this.scrollNext);
    }

    // Remove touch event listeners
    const emblaNode = this.querySelector('.embla');
    if (emblaNode) {
      emblaNode.removeEventListener('touchstart', this.handleTouchStart);
      emblaNode.removeEventListener('touchmove', this.handleTouchMove);
      emblaNode.removeEventListener('touchend', this.handleTouchEnd);
    }
  }

  init() {
    const mode = this.getAttribute('mode') || 'fade';
    this.autoplayDelay = parseInt(this.getAttribute('autoplay-delay')) || 5000;
    this.loop = this.getAttribute('loop') === 'true';
    const stopOnMouseEnter = this.getAttribute('stop-on-mouse-enter') === 'true';

    const emblaNode = this.querySelector('.embla');
    if (!emblaNode) return;

    this.slides = this.querySelectorAll('.embla__slide');
    if (this.slides.length === 0) return;

    if (mode === 'fade') {
      this.initFadeMode(stopOnMouseEnter);
    } else if (mode === 'slide') {
      this.initSlideMode(emblaNode, stopOnMouseEnter);
    }
  }

  // Vanilla JS fade mode implementation
  initFadeMode(stopOnMouseEnter) {
    this.currentIndex = 0;

    // Set initial active slide
    this.slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === 0);
    });

    // Start autoplay
    this.startAutoplay();

    // Setup navigation arrows
    this.setupFadeNavigation();

    // Setup touch/swipe events
    this.setupSwipeEvents();

    // Pause on hover
    if (stopOnMouseEnter) {
      this.addEventListener('mouseenter', () => {
        this.isHovered = true;
      });

      this.addEventListener('mouseleave', () => {
        this.isHovered = false;
      });
    }

    // Initial arrow state
    this.updateArrowStates();
  }

  startAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }

    this.autoplayInterval = setInterval(() => {
      if (!this.isHovered) {
        this.goToNextSlide();
      }
    }, this.autoplayDelay);
  }

  setupSwipeEvents() {
    const emblaNode = this.querySelector('.embla');
    if (emblaNode) {
      emblaNode.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      emblaNode.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      emblaNode.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }
  }

  handleTouchStart(e) {
    const touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  handleTouchMove(e) {
    if (!this.touchStartX || !this.touchStartY) return;
    
    const touch = e.touches[0];
    this.touchEndX = touch.clientX;
    this.touchEndY = touch.clientY;
  }

  handleTouchEnd(e) {
    if (!this.touchStartX || !this.touchEndX) return;

    const diffX = this.touchStartX - this.touchEndX;
    const diffY = this.touchStartY - this.touchEndY;

    if (Math.abs(diffX) > Math.abs(diffY)) {
      if (Math.abs(diffX) > this.minSwipeDistance) {
        if (diffX > 0) {
          // Left swipe - next
          this.goToNextSlide();
        } else {
          // Right swipe - previous
          this.goToPrevSlide();
        }
      }
    }

    this.touchStartX = null;
    this.touchStartY = null;
    this.touchEndX = null;
    this.touchEndY = null;
  }

  goToNextSlide() {
    if (!this.slides || this.slides.length === 0) return;

    this.slides[this.currentIndex].classList.remove('is-active');

    if (this.loop) {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    } else {
      if (this.currentIndex < this.slides.length - 1) {
        this.currentIndex++;
      }
    }

    this.slides[this.currentIndex].classList.add('is-active');
    this.updateArrowStates();
    
    // Restart autoplay timer
    this.startAutoplay();
  }

  goToPrevSlide() {
    if (!this.slides || this.slides.length === 0) return;

    this.slides[this.currentIndex].classList.remove('is-active');

    if (this.loop) {
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    } else {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    }

    this.slides[this.currentIndex].classList.add('is-active');
    this.updateArrowStates();
    
    // Restart autoplay timer
    this.startAutoplay();
  }

  setupFadeNavigation() {
    this.prevBtn = this.querySelector('.embla__prev');
    this.nextBtn = this.querySelector('.embla__next');

    if (!this.prevBtn || !this.nextBtn) return;

    this.prevBtn.addEventListener('click', this.handlePrevClick);
    this.nextBtn.addEventListener('click', this.handleNextClick);
  }

  handlePrevClick() {
    this.goToPrevSlide();
  }

  handleNextClick() {
    this.goToNextSlide();
  }

  updateArrowStates() {
    if (!this.prevBtn || !this.nextBtn || !this.slides) return;

    if (this.loop) {
      this.prevBtn.removeAttribute('disabled');
      this.nextBtn.removeAttribute('disabled');
    } else {
      if (this.currentIndex === 0) {
        this.prevBtn.setAttribute('disabled', 'disabled');
      } else {
        this.prevBtn.removeAttribute('disabled');
      }

      if (this.currentIndex === this.slides.length - 1) {
        this.nextBtn.setAttribute('disabled', 'disabled');
      } else {
        this.nextBtn.removeAttribute('disabled');
      }
    }
  }

  // Embla slide mode implementation
  initSlideMode(emblaNode, stopOnMouseEnter) {
    const options = {
      loop: this.loop,
      skipSnaps: false,
      duration: 25,
      axis: 'x',
      draggable: true,
    };

    const autoplayPlugin = window.EmblaCarouselAutoplay({
      delay: this.autoplayDelay,
      stopOnInteraction: false,
      stopOnMouseEnter: stopOnMouseEnter,
    });

    this.emblaApi = window.EmblaCarousel(emblaNode, options, [autoplayPlugin]);

    this.setupSlideNavigation();
  }

  scrollPrev() {
    if (this.emblaApi) {
      this.emblaApi.scrollPrev();
    }
  }

  scrollNext() {
    if (this.emblaApi) {
      this.emblaApi.scrollNext();
    }
  }

  togglePrevNextBtns() {
    if (!this.emblaApi || !this.prevBtn || !this.nextBtn) return;

    const canScrollPrev = this.emblaApi.canScrollPrev();
    const canScrollNext = this.emblaApi.canScrollNext();

    if (canScrollPrev) {
      this.prevBtn.removeAttribute('disabled');
    } else {
      this.prevBtn.setAttribute('disabled', 'disabled');
    }

    if (canScrollNext) {
      this.nextBtn.removeAttribute('disabled');
    } else {
      this.nextBtn.setAttribute('disabled', 'disabled');
    }
  }

  setupSlideNavigation() {
    this.prevBtn = this.querySelector('.embla__prev');
    this.nextBtn = this.querySelector('.embla__next');

    if (!this.prevBtn || !this.nextBtn || !this.emblaApi) return;

    this.prevBtn.addEventListener('click', this.scrollPrev);
    this.nextBtn.addEventListener('click', this.scrollNext);

    this.emblaApi.on('select', this.togglePrevNextBtns);
    this.emblaApi.on('init', this.togglePrevNextBtns);
    this.emblaApi.on('reInit', this.togglePrevNextBtns);
  }

  static get observedAttributes() {
    return ['mode', 'autoplay-delay', 'loop'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.cleanup();
      this.init();
    }
  }
}

if (!customElements.get('announcement-carousel')) {
  customElements.define('announcement-carousel', AnnouncementCarousel);
}